/**
 * Уведомления о звонке в шторке Android: входящий (полноэкранный и «тихий») и пропущенный.
 *
 * <p>Одно место на все три, потому что они меняются местами: полноэкранный входящий человек
 * может свернуть в тихий («Назад» на экране входящего), а любой из них после отбоя звонящего
 * превращается в «Пропущенный звонок». У всех один и тот же набор полей в `data` — по нему
 * кнопки в шторке понимают, куда звонить и кому писать
 * ({@link import('./callNotificationActions').decideCallNotificationAction}).
 *
 * <p>Вне Android ничего не делает: на iOS входящий рисует CallKit, в вебе шторки нет.
 */
import notifee, {
  AndroidCategory,
  AndroidImportance,
  AndroidVisibility,
} from '@notifee/react-native';
import { Platform } from 'react-native';
import { incomingCallHeadline } from '../calls/callLabels';
import type { IncomingCallUIPayload } from '../calls/incomingCallUiBridge';
import { voiceCallLog, voiceCallWarn } from '../calls/voiceCallDebug';
import { tr } from '../i18n/runtime';
import { markMissedCallForNudge } from './androidNotifyOnboarding';
import {
  INCOMING_CALL_NOTIFICATION_ID,
  ensureCallsChannel,
  ensureMessageChannel,
} from './androidChannels';
import {
  CALL_ACTION_ANSWER,
  CALL_ACTION_CALL_BACK,
  CALL_ACTION_DECLINE,
  CALL_ACTION_MESSAGE,
  CALL_NOTIFICATION_TYPE_INCOMING,
  CALL_NOTIFICATION_TYPE_MISSED,
  incomingPayloadFromNotificationData,
} from './callNotificationActions';

/**
 * Канал «тихого» входящего: всплывает (HIGH), но без звука и вибрации. Звук у канала задаётся раз
 * и навсегда, поэтому отдельный канал, а не флаг на уведомлении. Имя id намеренно вне схемы
 * `calls_v2_*` из `androidChannels`: тамошняя прополка удалила бы его как «канал прошлой темы».
 */
const QUIET_CALLS_CHANNEL_ID = 'calls_quiet_v1';

let quietChannelReady = false;

async function ensureQuietCallsChannel(): Promise<string> {
  if (!quietChannelReady) {
    await notifee.createChannel({
      id: QUIET_CALLS_CHANNEL_ID,
      name: tr('notifyChannelCalls'),
      importance: AndroidImportance.HIGH,
      vibration: false,
    });
    quietChannelReady = true;
  }
  return QUIET_CALLS_CHANNEL_ID;
}

/** `data` уведомления — только строки: так требует notifee. */
export function callNotificationData(
  type: string,
  payload: IncomingCallUIPayload,
): Record<string, string> {
  const data: Record<string, string> = {
    type,
    chatId: payload.chatId,
    callerName: payload.callerName,
    callMode: payload.callMode,
    callScope: payload.callScope,
  };
  if (payload.inviteId) data.inviteId = payload.inviteId;
  // `title` — то же поле, что в пуше сервера: по нему тап восстановит название группы, а
  // «Написать» из пропущенного откроет чат под правильным именем.
  data.title = payload.callScope === 'group' && payload.groupTitle ? payload.groupTitle : payload.callerName;
  if (payload.callerUserId) {
    data.callerId = payload.callerUserId;
    data.callerHasAvatar = payload.callerHasAvatar ? '1' : '0';
    data.callerAvatarRevision = String(payload.callerAvatarRevision ?? 0);
    if (payload.callerAvatarShape) data.callerAvatarShape = payload.callerAvatarShape;
  }
  return data;
}

/**
 * Показать входящий в шторке.
 *
 * @param opts.quiet «тихий» вариант: человек нажал «Назад» на экране входящего — рингтон он уже
 *     заглушил, и звонить шторкой заново, да ещё на весь экран, было бы против его воли. Звонок
 *     при этом остаётся: ответить и отклонить можно прямо из шторки, тап открывает экран снова.
 */
export async function showIncomingCallNotification(
  payload: IncomingCallUIPayload,
  opts: { quiet?: boolean } = {},
): Promise<void> {
  if (Platform.OS !== 'android') return;
  const channelId = opts.quiet ? await ensureQuietCallsChannel() : await ensureCallsChannel();
  // В группе заголовок — её название, а кто звонит — в тексте (E2E-8).
  const headline = incomingCallHeadline(payload);
  const body = opts.quiet
    ? tr('callNotifySilencedBody')
    : headline.callerName
      ? tr('callGroupIncomingFrom').replace('{name}', headline.callerName)
      : tr('callIncomingFull');
  await notifee.displayNotification({
    /**
     * Постоянный id, а не сгенерированный: по нему in-app оверлей снимает уведомление,
     * когда берёт звонок на себя, а отмена звонка — когда звонящий передумал.
     */
    id: INCOMING_CALL_NOTIFICATION_ID,
    title: headline.title,
    body,
    data: callNotificationData(CALL_NOTIFICATION_TYPE_INCOMING, payload),
    android: {
      channelId,
      importance: AndroidImportance.HIGH,
      category: AndroidCategory.CALL,
      ...(opts.quiet ? {} : { fullScreenAction: { id: 'incoming-call-fullscreen' } }),
      visibility: AndroidVisibility.PUBLIC,
      ongoing: true,
      autoCancel: false,
      showTimestamp: false,
      /**
       * Звонок обязан звонить, а не звякать один раз. Канал задаёт файл (рингтон темы),
       * а зацикливание — свойство самого уведомления.
       */
      loopSound: !opts.quiet,
      pressAction: { id: 'default', launchActivity: 'default' },
      actions: [
        // «Отклонить» без открытия приложения: отказ уходит запросом к серверу.
        { title: tr('callNotifyDecline'), pressAction: { id: CALL_ACTION_DECLINE } },
        {
          title: tr('callNotifyAnswer'),
          pressAction: { id: CALL_ACTION_ANSWER, launchActivity: 'default' },
        },
      ],
    },
  });
}

function missedNotificationId(chatId: string): string {
  return `missed-call-${chatId}`;
}

/** «Пропущенный звонок от X» с «Перезвонить» (личный) и «Написать». */
export async function showMissedCallNotification(payload: IncomingCallUIPayload): Promise<void> {
  if (Platform.OS !== 'android') return;
  const channelId = await ensureMessageChannel();
  const group = payload.callScope === 'group';
  const title = group
    ? tr('callNotifyMissedGroupTitle')
    : payload.callMode === 'video'
      ? tr('callNotifyMissedVideoTitle')
      : tr('callNotifyMissedTitle');
  const who = group && payload.groupTitle ? payload.groupTitle : payload.callerName;
  // Повод попросить про полноэкранные уведомления — при следующем открытии приложения.
  void markMissedCallForNudge();
  await notifee.displayNotification({
    id: missedNotificationId(payload.chatId),
    title,
    body: tr('callNotifyMissedFrom').replace('{name}', who),
    data: callNotificationData(CALL_NOTIFICATION_TYPE_MISSED, payload),
    android: {
      channelId,
      importance: AndroidImportance.HIGH,
      category: AndroidCategory.CALL,
      autoCancel: true,
      pressAction: { id: 'default', launchActivity: 'default' },
      actions: [
        ...(group
          ? []
          : [
              {
                title: tr('callNotifyCallBack'),
                pressAction: { id: CALL_ACTION_CALL_BACK, launchActivity: 'default' },
              },
            ]),
        {
          title: tr('callNotifyMessage'),
          pressAction: { id: CALL_ACTION_MESSAGE, launchActivity: 'default' },
        },
      ],
    },
  });
}

/** Убрать «пропущенный» из этого чата — по нему уже перезвонили или написали. */
export async function dismissMissedCallNotification(chatId: string): Promise<void> {
  if (Platform.OS !== 'android') return;
  try {
    await notifee.cancelNotification(missedNotificationId(chatId));
  } catch {
    /* уведомления могло не быть */
  }
}

/**
 * Звонящий положил трубку, а входящий в этот момент висел в шторке — превратить его в
 * «Пропущенный звонок».
 *
 * <p>Только если в шторке действительно висел входящий <b>этого</b> чата: значит, человек
 * звонок не видел (приложение свёрнуто) или сам убрал его туда «Назад». Если же звонок звонил
 * на открытом экране входящего, человек его видел — «пропущенный» в шторке был бы лишним.
 * Заглушённый чат сюда не доходит сам: входящий из него в шторку не попадает вовсе.
 *
 * <p>Вызывать до снятия уведомления о входящем — иначе проверять будет нечего.
 */
export async function replaceIncomingWithMissedCallNotification(chatId: string): Promise<void> {
  if (Platform.OS !== 'android') return;
  try {
    const shown = await notifee.getDisplayedNotifications();
    const incoming = shown.find((n) => n.notification?.id === INCOMING_CALL_NOTIFICATION_ID);
    const data = (incoming?.notification?.data ?? null) as Record<string, unknown> | null;
    if (!data || data.chatId !== chatId) return;
    const payload = incomingPayloadFromNotificationData(data);
    if (!payload) return;
    await showMissedCallNotification(payload);
    voiceCallLog('пропущенный звонок показан в шторке', { chatId });
  } catch (e) {
    voiceCallWarn('missed call notification failed', e);
  }
}
